import {
  getExclusionOptions,
  getExclusionTypes,
  getReportsTo,
  getRoleOptions,
  getSuperAdminReportsTo,
  getUserById,
  getUserManagementData,
  getUserPickerOptions,
  UserManagementQueryKeys,
} from "@/api/user-management/userManagement.queries";
import { useActingEntityId } from "@/hooks/useActingEntity";
import { useActiveSession } from "@/hooks/useActiveSession";
import { useRequestScope } from "@/hooks/useRequestScope";
import { actingTenantFor } from "@/api/access/access.queries";
import { useImpersonationStore } from "@/store/useImpersonationStore";
import { uniq } from "@/utils/arrayUtils";
import { useDebouncedValue } from "@mantine/hooks";
import { keepPreviousData, useInfiniteQuery, useQueries, useQuery } from "@tanstack/react-query";
import { useMemo, useState } from "react";

export const useUserManagementData = ({
  search,
  sortBy,
  sortOrder,
  page,
  pageSize,
  filters,
}: {
  search?: string;
  sortBy?: string;
  sortOrder?: "asc" | "desc";
  page?: string;
  pageSize?: string;
  filters?: Array<{ filterBy: string; filterValue: { id: string; value: string } }>;
}) => {
  return useQuery({
    queryKey: [UserManagementQueryKeys.roles, search, sortBy, sortOrder, page, pageSize, filters],
    queryFn: ({ signal }) =>
      getUserManagementData({
        search,
        sortBy,
        sortOrder,
        page,
        pageSize,
        filters,
        signal,
      }),
    placeholderData: keepPreviousData,
  });
};

const REPORTS_TO_PAGE_SIZE = 500;
const ROLE_OPTIONS_PAGE_SIZE = 10;

export const useReportsTo = (
  selectedOption?: { value: string; label: string } | null,
  isMainAppLoginSuperAdmin = false,
  excludeUserId?: string,
  /** The company whose people are listed, when it is not the request's own (UI31). */
  actingEntityId?: string,
) => {
  const [searchValue, setSearchValue] = useState("");
  const [debouncedSearchValue] = useDebouncedValue(searchValue, 500);

  const queryKey = isMainAppLoginSuperAdmin
    ? [
        UserManagementQueryKeys.superAdminReportTo,
        encodeURIComponent(debouncedSearchValue),
        excludeUserId ?? "",
      ]
    : [
        UserManagementQueryKeys.reportTo,
        encodeURIComponent(debouncedSearchValue),
        excludeUserId ?? "",
        actingEntityId ?? "",
      ];

  const fetchFn = isMainAppLoginSuperAdmin ? getSuperAdminReportsTo : getReportsTo;

  const {
    data,
    isLoading,
    error,
    isError,
    isFetching,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    refetch,
  } = useInfiniteQuery({
    queryKey,
    initialPageParam: 1,
    queryFn: ({ pageParam = 1, signal }) =>
      fetchFn({
        page: pageParam,
        pageSize: REPORTS_TO_PAGE_SIZE,
        search: debouncedSearchValue,
        signal,
        excludeUserId,
        actingEntityId,
      }),
    getNextPageParam: (lastPage) => {
      const nextPage = lastPage.page + 1;
      return nextPage <= lastPage.totalPages ? nextPage : undefined;
    },
  });

  const options = useMemo(() => {
    if (isLoading) return [{ label: "Loading", value: "loading", disabled: true }];

    if (!data?.pages[0].total)
      return [{ label: "No data found", value: "no-data", disabled: true }];

    const allOptions = data?.pages.flatMap((page) => page.data) ?? [];

    // If there's a selected option and it's not in the current options list, add it
    if (selectedOption && !allOptions.some((opt) => opt.value === selectedOption.value)) {
      return [selectedOption, ...allOptions];
    }

    return uniq(allOptions, "value");
  }, [data, selectedOption, isLoading]);

  return {
    options,
    searchValue,
    onSearchChange: setSearchValue,
    isLoading,
    error,
    isError,
    isFetching,
    fetchNextPage,
    hasNextPage: !!hasNextPage,
    isFetchingNextPage,
    refetch,
  };
};

export const useRoleOptions = () => {
  const [searchValue, setSearchValue] = useState("");
  const [debouncedSearchValue] = useDebouncedValue(searchValue, 1000);
  const { tenantId, userType } = useActiveSession();
  const requestScope = useRequestScope();
  const isImpersonating = useImpersonationStore((state) => state.isImpersonating);
  // SERV-3550: the roles of the entity the request is answered in (a label-menu pick, never while
  // impersonating), else the session's own; and in the key, so a switch never serves another's.
  const roleTenant =
    actingTenantFor(requestScope, { userType, isImpersonating }) ?? tenantId ?? undefined;

  const {
    data,
    isLoading,
    error,
    isError,
    isFetching,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    refetch,
  } = useInfiniteQuery({
    queryKey: [
      UserManagementQueryKeys.roleOptions,
      roleTenant ?? "",
      encodeURIComponent(debouncedSearchValue),
    ],
    initialPageParam: 1,
    queryFn: ({ pageParam = 1, signal }) =>
      getRoleOptions({
        page: pageParam,
        pageSize: ROLE_OPTIONS_PAGE_SIZE,
        search: debouncedSearchValue,
        signal,
        tenantId: roleTenant,
      }),
    getNextPageParam: (lastPage) => {
      const nextPage = lastPage.page + 1;
      return nextPage <= lastPage.totalPages ? nextPage : undefined;
    },
  });

  const options = useMemo(() => {
    if (isLoading) return [{ label: "Loading", value: "loading", disabled: true }];

    if (!data?.pages[0].total)
      return [{ label: "No data found", value: "no-data", disabled: true }];

    const allOptions = data?.pages.flatMap((page) => page.data) ?? [];
    return uniq(allOptions, "value");
  }, [data, isLoading]);

  return {
    data: options,
    isLoading,
    error,
    isError,
    isFetching,
    searchValue,
    onSearchChange: setSearchValue,
    fetchNextPage,
    hasNextPage: !!hasNextPage,
    isFetchingNextPage,
    refetch,
  };
};

export const useExclusionTypes = () => {
  return useQuery({
    queryKey: [UserManagementQueryKeys.exclusions],
    queryFn: getExclusionTypes,
  });
};

export const useExclusionOptions = ({
  exclusionType,
  page = 1,
  pageSize = 99,
}: {
  exclusionType: string;
  page?: number;
  pageSize?: number;
}) => {
  return useQuery({
    queryKey: [UserManagementQueryKeys.exclusionTypes, exclusionType, page, pageSize],
    queryFn: ({ signal }) => getExclusionOptions({ exclusionType, page, pageSize, signal }),
    enabled: !!exclusionType,
  });
};

/**
 * Fetches exclusion options (e.g. clients, brands) for each rule type.
 * When idToValueMap is provided: ruleTypeIds are category ids; API is called with value only.
 * We only fetch when we have the value (so edit mode waits for exclusion types before calling dropdown API with value).
 * When not provided: ruleTypeIds are sent as exclusionType (create flow).
 */
export const useExclusionOptionsByTypes = (
  ruleTypeIds: string[],
  idToValueMap?: Record<string, string>,
) => {
  const idsAndExclusionTypes = useMemo(() => {
    if (idToValueMap) {
      const idsWithValue = ruleTypeIds.filter((id) => idToValueMap[id] != null);
      return idsWithValue.map((id) => ({ id, exclusionType: idToValueMap[id] as string }));
    }
    return ruleTypeIds.map((id) => ({ id, exclusionType: id }));
  }, [ruleTypeIds, idToValueMap]);

  const queries = useQueries({
    queries: idsAndExclusionTypes.map(({ id, exclusionType }) => ({
      queryKey: [UserManagementQueryKeys.exclusionTypes, id, exclusionType, 1, 500],
      queryFn: ({ signal }) =>
        getExclusionOptions({ exclusionType, page: 1, pageSize: 500, signal }),
    })),
  });

  const optionsByRuleType = useMemo(() => {
    const map: Record<string, { label: string; value: string }[]> = {};
    queries.forEach((q, i) => {
      const { id } = idsAndExclusionTypes[i];
      if (q.data?.data) map[id] = q.data.data;
    });
    return map;
  }, [queries, idsAndExclusionTypes]);

  const isLoadingByRuleType = useMemo(() => {
    const map: Record<string, boolean> = {};
    idsAndExclusionTypes.forEach(({ id }, i) => {
      map[id] = queries[i]?.isLoading === true;
    });
    return map;
  }, [queries, idsAndExclusionTypes]);

  return { optionsByRuleType, isLoadingByRuleType };
};

const ENTITY_ACCESS_PAGE_SIZE = 500; // sending limit as 500 as BE requested

/** Paginated entity dropdown for add-user entity access MultiSelect (infinite scroll). */
// `enabled` defaults to true for the dedicated add-user route, where the fetch
// should always run. Callers that mount the hook behind a closed modal pass
// false so a 500-row entity fetch does not fire on every render of the parent.
export const useEntityAccessOptions = (enabled = true) => {
  const {
    data,
    isLoading,
    error,
    isError,
    isFetching,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    refetch,
  } = useInfiniteQuery({
    enabled,
    queryKey: [UserManagementQueryKeys.exclusionTypes, "entity", ENTITY_ACCESS_PAGE_SIZE],
    initialPageParam: 1,
    queryFn: ({ pageParam = 1, signal }) =>
      getExclusionOptions({
        exclusionType: "entity",
        page: pageParam,
        pageSize: ENTITY_ACCESS_PAGE_SIZE,
        signal,
      }),
    getNextPageParam: (lastPage) => {
      const nextPage = lastPage.page + 1;
      return nextPage <= lastPage.totalPages ? nextPage : undefined;
    },
  });

  const options = useMemo(() => {
    if (isLoading) return [{ label: "Loading", value: "loading", disabled: true }];

    if (!data?.pages[0]?.total)
      return [{ label: "No data found", value: "no-data", disabled: true }];

    const allOptions = data?.pages.flatMap((page) => page.data) ?? [];
    return uniq(allOptions, "value");
  }, [data, isLoading]);

  // The real row count, before `options` above pads an empty or in-flight result
  // with its "Loading" / "No data found" placeholder. Callers that need to know
  // how many entities this session may actually grant have to read this, not
  // `options.length`.
  const total = data?.pages[0]?.total ?? 0;

  return {
    data: options,
    total,
    isLoading,
    error,
    isError,
    isFetching,
    fetchNextPage,
    hasNextPage: !!hasNextPage,
    isFetchingNextPage,
    refetch,
  };
};

/** Fetches a single user by id (for edit page). Enabled only when userId is non-empty. */
export const useUserById = (userId: string | undefined) => {
  return useQuery({
    queryKey: [UserManagementQueryKeys.userDetail, userId],
    queryFn: ({ signal }) => getUserById(userId!, signal),
    enabled: Boolean(userId),
  });
};

/**
 * Users for a picker that names people rather than roles.
 *
 * Backed by the dropdown read, which excludes service providers and scopes by
 * active entity membership. `entityIds` is for a caller acting for entities it
 * is not inside; the backend ignores it for an entity login, which is already
 * pinned to its own tenant.
 */
export const useUserPickerOptions = (enabled = true, entityIds?: string[]) => {
  const [searchValue, setSearchValue] = useState("");
  const [debouncedSearch] = useDebouncedValue(searchValue, 300);
  const { userId } = useActiveSession();
  const actingEntityId = useActingEntityId();

  // Sorted before it becomes a key, so reordering the same set is not a refetch.
  const entityKey = entityIds?.length ? [...entityIds].sort().join(",") : "caller-entity";

  const query = useQuery({
    // The acting user is part of the key because the response is scoped server
    // side by the caller's entity membership. Without it, "caller-entity" is a
    // literal shared by every entity login, and impersonation swaps identity
    // without evicting anything, so a picker mounted after a swap rendered the
    // previous identity's users. On this screen those are offered as permitted
    // payers of another entity's account.
    queryKey: [
      UserManagementQueryKeys.userPickerOptions,
      debouncedSearch,
      entityKey,
      userId ?? "anonymous",
      actingEntityId ?? "caller-scope",
    ],
    queryFn: ({ signal }) =>
      getUserPickerOptions(debouncedSearch || undefined, signal, entityIds, actingEntityId),
    enabled,
    placeholderData: keepPreviousData,
  });

  return { ...query, searchValue, setSearchValue };
};
